import React, { useMemo } from 'react';
import { useFieldArray } from 'react-hook-form';
import StepHeading, { SectionCard, InfoNotice } from '../StepHeading';
import StepFooter from '../StepFooter';
import CoverageMeter from '../CoverageMeter';
import { useStepForm } from '../useStepForm';
import { TextInput, SelectInput, TextArea, MonthInput, PhoneInput, ZipInput, YesNoToggle, CheckboxInline, RepeatableGroup, ErrorSummary } from '../../ui/form';
import { US_STATES } from '../../../utils/usStates';
import { computeCoverage, validateMonthRange } from '../../../utils/applicationCoverage';
import { formatPhoneInput, phoneDigits, isValidMonth } from '../../../utils/dateInput';

const emptyEmployer = () => ({
  employerName: '', contactPerson: '', address: '', city: '', state: '', zipCode: '', phone: '', fax: '', email: '',
  from: '', to: '', isPresent: false, positionHeld: '', reasonForLeaving: '', dotRegulated: null, safetySensitive: null
});

const toFormValues = (application) => {
  const emp = application.sections?.employment || {};
  const bool = (v) => (typeof v === 'boolean' ? v : null);
  const employers = (emp.employers || []).map((e) => ({ ...emptyEmployer(), ...e, phone: formatPhoneInput(e.phone || ''), dotRegulated: bool(e.dotRegulated), safetySensitive: bool(e.safetySensitive), isPresent: Boolean(e.isPresent) }));
  if (!employers.length) employers.push(emptyEmployer());
  return {
    employment: {
      drugAlcohol: {
        testedPositiveOrRefusedLast2Years: bool(emp.drugAlcohol?.testedPositiveOrRefusedLast2Years),
        canProvideReturnToDutyDocs: emp.drugAlcohol?.canProvideReturnToDutyDocs || null
      },
      employers,
      gapExplanation: emp.gapExplanation || ''
    }
  };
};

const toPayload = (values) => ({
  employment: {
    drugAlcohol: {
      testedPositiveOrRefusedLast2Years: values.employment?.drugAlcohol?.testedPositiveOrRefusedLast2Years,
      canProvideReturnToDutyDocs: values.employment?.drugAlcohol?.canProvideReturnToDutyDocs || ''
    },
    employers: (values.employment?.employers || []).map((e) => ({
      ...e,
      phone: phoneDigits(e.phone),
      state: (e.state || '').toUpperCase(),
      to: e.isPresent ? null : (e.to || ''),
      isPresent: Boolean(e.isPresent),
      reasonForLeaving: e.isPresent ? '' : (e.reasonForLeaving || '')
    })),
    gapExplanation: values.employment?.gapExplanation || ''
  }
});

const LABELS = {
  'employment.drugAlcohol.testedPositiveOrRefusedLast2Years': 'Drug and alcohol question 1',
  'employment.drugAlcohol.canProvideReturnToDutyDocs': 'Drug and alcohol question 2',
  'employment.employers': 'Employers',
  'employment.gapExplanation': 'Employment gaps',
  'employment.employers.*.employerName': 'Employer {n}: name', 'employment.employers.*.phone': 'Employer {n}: phone', 'employment.employers.*.zipCode': 'Employer {n}: ZIP code',
  'employment.employers.*.email': 'Employer {n}: email', 'employment.employers.*.from': 'Employer {n}: date from', 'employment.employers.*.to': 'Employer {n}: date to',
  'employment.employers.*.positionHeld': 'Employer {n}: position held', 'employment.employers.*.reasonForLeaving': 'Employer {n}: reason for leaving',
  'employment.employers.*.dotRegulated': 'Employer {n}: DOT regulated', 'employment.employers.*.safetySensitive': 'Employer {n}: safety-sensitive'
};

const RTD_OPTIONS = [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'na', label: 'N/A' }];

const EmploymentStep = ({ application, locked, flaggedMessage, onSaveDraft, onComplete, onContinue, onBack }) => {
  const defaultValues = useMemo(() => toFormValues(application), [application.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const hasCmv = Boolean(application.sections?.experience?.hasDrivenCmvBefore);
  const requiredMonths = hasCmv ? 120 : 36;

  const validate = (values) => {
    const problems = [];
    const emp = values.employment || {};
    if (emp.drugAlcohol?.testedPositiveOrRefusedLast2Years === true && emp.drugAlcohol?.canProvideReturnToDutyDocs === 'na') {
      problems.push({ name: 'employment.drugAlcohol.canProvideReturnToDutyDocs', message: 'Answer Yes or No since you answered Yes above' });
    }
    (emp.employers || []).forEach((e, index) => {
      const problem = validateMonthRange({ from: e.from, to: e.to, isPresent: e.isPresent });
      if (problem) problems.push({ name: `employment.employers.${index}.from`, message: problem });
    });
    if (!problems.length) {
      const coverage = computeCoverage({ entries: (emp.employers || []).map((e) => ({ from: e.from, to: e.to, isPresent: e.isPresent })), requiredMonths });
      if (coverage.gapsNeedingExplanation.length && !emp.gapExplanation?.trim()) {
        problems.push({ name: 'employment.gapExplanation', message: `Explain the gap${coverage.gapsNeedingExplanation.length > 1 ? 's' : ''}: ${coverage.gapsNeedingExplanation.map((g) => `${g.from} to ${g.to}`).join(', ')}` });
      }
    }
    return problems;
  };

  const { form, values, errors, autosave, submitting, serverMessage, failedAttempts, submitNext } = useStepForm({
    stepId: 'employment', defaultValues, toPayload, onSaveDraft, onComplete, locked, validate
  });
  const { register, control } = form;
  const employers = useFieldArray({ control, name: 'employment.employers' });

  const coverage = useMemo(() => computeCoverage({
    entries: (values.employment?.employers || []).filter((e) => isValidMonth(e.from) && (e.isPresent || isValidMonth(e.to))).map((e) => ({ from: e.from, to: e.to, isPresent: e.isPresent })),
    requiredMonths
  }), [values.employment?.employers, requiredMonths]);

  const testedPositive = values.employment?.drugAlcohol?.testedPositiveOrRefusedLast2Years;

  return (
    <form onSubmit={(e) => { e.preventDefault(); submitNext(); }} noValidate>
      <StepHeading title="Employment" description="Your drug and alcohol testing statement and your employment history." flaggedMessage={flaggedMessage} locked={locked} />
      <ErrorSummary errors={errors} labels={LABELS} focusKey={failedAttempts} className="mb-5" />
      {serverMessage && !Object.keys(errors).length && <p className="mb-4 text-sm text-red-600" role="alert">{serverMessage}</p>}

      <fieldset disabled={locked} className="space-y-5">
        <SectionCard title="Pre-employment alcohol & drug test statement">
          <InfoNotice title="Important notice, please read carefully" tone="warning">
            49 CFR Part 40.25(j) states that an employer must ask the employee whether he or she has tested positive, or refused to test, on any pre-employment drug or alcohol test administered by an employer to which the employee applied for, but did not obtain, safety-sensitive transportation work covered by DOT agency drug and alcohol testing rules during the past two years.
          </InfoNotice>
          <div className="mt-4 space-y-5">
            <YesNoToggle name="employment.drugAlcohol.testedPositiveOrRefusedLast2Years" control={control} required label="1. Have you tested positive, or refused to test, on any pre-employment drug or alcohol test administered by an employer to which you applied for, but did not obtain, safety-sensitive transportation work covered by DOT agency drug and alcohol testing rules during the past two years?" />
            <YesNoToggle name="employment.drugAlcohol.canProvideReturnToDutyDocs" control={control} required options={RTD_OPTIONS} label="2. If you answered yes to the above question, can you provide documentation of successful completion of DOT return-to-duty requirements (including follow-up tests)?" hint={testedPositive === false ? 'Choose N/A if question 1 was No.' : undefined} />
          </div>
        </SectionCard>

        <SectionCard
          title={hasCmv ? 'List all employers for the previous 10 years' : 'List all employers for the previous 3 years'}
          description={`The Federal Motor Carrier Safety Regulations (49 CFR 391.21) require all applicants wishing to drive a commercial vehicle to list all employment for the last three (3) years.${hasCmv ? ' Because you have driven a commercial vehicle, you must also provide employment history for an additional seven (7) years (ten years total).' : ''} Any gaps in employment in excess of one (1) month must be explained.`}
        >
          <CoverageMeter coverage={coverage} label="Employment history" gapLabel="employment gap" className="mb-4" />
          {errors.employment?.employers?.message && <p className="mb-3 text-sm text-red-600" role="alert">{errors.employment.employers.message}</p>}
          <RepeatableGroup
            items={employers.fields}
            title={(i) => (values.employment?.employers?.[i]?.employerName ? `${values.employment.employers[i].employerName}` : `Employer ${i + 1}`)}
            addLabel="Add another employer"
            onAdd={() => employers.append(emptyEmployer())}
            onRemove={employers.remove}
            minItems={1}
            maxItems={25}
            disabled={locked}
            renderItem={(index) => {
              const e = errors.employment?.employers?.[index] || {};
              const base = `employment.employers.${index}`;
              const isPresent = Boolean(values.employment?.employers?.[index]?.isPresent);
              return (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <TextInput label="Employer name" required {...register(`${base}.employerName`, { required: 'Employer name is required' })} error={e.employerName?.message} />
                  <TextInput label="Contact person" optionalLabel {...register(`${base}.contactPerson`)} />
                  <TextInput label="Address" optionalLabel autoComplete="off" {...register(`${base}.address`)} />
                  <TextInput label="City" optionalLabel {...register(`${base}.city`)} />
                  <SelectInput label="State" optionalLabel options={US_STATES} {...register(`${base}.state`)} />
                  <ZipInput label="ZIP code" optionalLabel {...register(`${base}.zipCode`, { validate: (v) => !v || /^\d{5}(-\d{4})?$/.test(v) || 'Enter a 5-digit ZIP code' })} error={e.zipCode?.message} />
                  <PhoneInput label="Phone number" required {...register(`${base}.phone`, { required: 'Employer phone is required', validate: (v) => phoneDigits(v).length === 10 || 'Enter a 10-digit phone number' })} error={e.phone?.message} />
                  <TextInput label="Fax" optionalLabel type="tel" {...register(`${base}.fax`)} />
                  <TextInput label="Email address" optionalLabel type="email" autoComplete="off" {...register(`${base}.email`, { pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' } })} error={e.email?.message} />
                  <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                    <MonthInput label="Date from" required {...register(`${base}.from`, { required: 'Required', validate: (v) => isValidMonth(v) || 'Choose a month and year' })} error={e.from?.message} />
                    <div>
                      <MonthInput label="Date to" required={!isPresent} disabled={isPresent} {...register(`${base}.to`, { validate: (v) => isPresent || (!v ? 'Required' : (isValidMonth(v) || 'Choose a month and year')) })} error={e.to?.message} />
                      <CheckboxInline className="mt-1" label="Present" {...register(`${base}.isPresent`)} />
                    </div>
                  </div>
                  <TextInput label="Position held" required {...register(`${base}.positionHeld`, { required: 'Position held is required' })} error={e.positionHeld?.message} />
                  <TextInput label="Reason for leaving" required={!isPresent} optionalLabel={isPresent} disabled={isPresent} {...register(`${base}.reasonForLeaving`, { validate: (v) => isPresent || Boolean(v?.trim()) || 'Reason for leaving is required' })} error={e.reasonForLeaving?.message} />
                  <YesNoToggle name={`${base}.dotRegulated`} control={control} required className="md:col-span-2" label={<span>Were you subject to the DOT/FMCSA regulations while employed by this carrier? <strong>If you were a driver, choose Yes.</strong></span>} />
                  <YesNoToggle name={`${base}.safetySensitive`} control={control} required className="md:col-span-2" label={<span>Was your job designated as a safety-sensitive function, in any DOT regulated mode, subject to the alcohol and controlled substances testing requirements of 49 CFR Part 40? <strong>If you were a driver, choose Yes.</strong></span>} />
                </div>
              );
            }}
          />
          <TextArea
            className="mt-5"
            label="Explain any gaps in employment longer than one (1) month. Include month/year and reason."
            optionalLabel={!coverage.gapsNeedingExplanation.length}
            required={coverage.gapsNeedingExplanation.length > 0}
            rows={3}
            placeholder={coverage.gapsNeedingExplanation.length ? `e.g. ${coverage.gapsNeedingExplanation[0].from} to ${coverage.gapsNeedingExplanation[0].to}: attended CDL school` : 'e.g. 03/2024 to 06/2024: medical leave'}
            {...register('employment.gapExplanation')}
            error={errors.employment?.gapExplanation?.message}
          />
        </SectionCard>
      </fieldset>

      <StepFooter onBack={onBack} onNext={locked ? onContinue : submitNext} isSubmitting={submitting} saveStatus={autosave.status} lastSavedAt={autosave.lastSavedAt} onRetrySave={autosave.saveNow} nextDisabled={locked && !onContinue} nextLabel={locked ? 'Continue' : 'Save & continue'} />
    </form>
  );
};

export default EmploymentStep;
